import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { Switch } from "../components/ui/switch"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"

/** Extract last segment from a bind path. E.g., "#{settings.darkMode}" -> "darkMode" */
function extractFieldName(bind?: string): string | undefined {
  if (!bind) return undefined
  const stripped = bind.replace(/^#\{/, "").replace(/\}$/, "")
  const segments = stripped.split(".")
  return segments[segments.length - 1]
}

export function SwitchRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("switch")
  const resolvedVariant = (element as { variant?: string }).variant
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const textSource = element.label ?? element.text
  const text = textSource ? String(resolveSymbol(textSource) ?? "") : undefined

  const defaultChecked = element.defaultChecked === true || element.checked === true
  const [checked, setChecked] = useBoundValue<boolean>(
    element.bind,
    resolveSymbol,
    defaultChecked,
    Boolean,
  )

  const handleCheckedChange = (value: boolean) => {
    setChecked(value)
    // forward per-item itemContext (parity with button/checkbox) so a block-ref'd row keeps item identity
    dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value })
  }

  const switchClassName = stripPresetConflicts(element.className, undefined, element.designAuthorityClassName)

  if (!text) {
    return (
      <Switch
        id={element.id}
        checked={checked}
        onCheckedChange={handleCheckedChange}
        size={resolvedSize as "sm" | "default"}
        className={switchClassName}
        data-testid={`switch-${element.id}`}
        data-action="toggle"
        data-field-name={extractFieldName(element.bind)}
        data-field-type="switch"
        data-atom-type="switch"
        data-variant={resolvedVariant}
      />
    )
  }

  return (
    <div className={cn("flex items-center space-x-2", switchClassName)} data-testid={`switch-${element.id}`} data-action="toggle" data-field-name={extractFieldName(element.bind)} data-field-type="switch" data-atom-type="switch" data-variant={resolvedVariant} data-size={resolvedSize}>
      <Switch id={element.id} checked={checked} onCheckedChange={handleCheckedChange} size={resolvedSize as "sm" | "default"} />
      <label htmlFor={element.id} className="text-sm font-medium leading-none">{text}</label>
    </div>
  )
}
